import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

// Store focus state globally so it persists across controller reconnections
let pendingFocusRestore: { cursorPos: number } | null = null;

// Track mode-bar visibility across reconnects so we only play the entrance
// animation on true appear/disappear — not on every filters re-render.
let modeBarWasVisible = false;

export default class CatalogFilterController extends Controller {
  static targets = [
    // Search elements (can be multiple - desktop and mobile)
    "searchForm",
    "searchInput",
    // Mobile bottom sheet elements
    "mobileTrigger",
    "backdrop",
    "bottomSheet",
    "mobileForm",
    // Pills
    "pillsContainer",
    // Mode tab bar wrapper
    "modeBar",
  ];

  static values = {
    formUrl: String,
  };

  declare readonly searchFormTargets: HTMLFormElement[];
  declare readonly searchInputTargets: HTMLInputElement[];
  declare readonly mobileTriggerTarget: HTMLButtonElement;
  declare readonly backdropTarget: HTMLElement;
  declare readonly bottomSheetTarget: HTMLElement;
  declare readonly mobileFormTarget: HTMLFormElement;
  declare readonly pillsContainerTarget: HTMLElement;
  declare readonly modeBarTarget: HTMLElement;
  declare readonly hasModeBarTarget: boolean;

  declare readonly hasBackdropTarget: boolean;
  declare readonly hasBottomSheetTarget: boolean;
  declare readonly hasMobileFormTarget: boolean;
  declare readonly hasPillsContainerTarget: boolean;

  declare readonly formUrlValue: string;

  private debounceTimer: number | null = null;
  private readonly DEBOUNCE_DELAY = 400; // Slightly longer for better UX
  private readonly SKELETON_DELAY_MS = 350;
  private readonly REVEAL_DURATION_MS = 200;
  private isDragging = false;
  private dragStartY = 0;
  private skeletonTimer: number | null = null;
  private revealTimer: number | null = null;
  private abortController: AbortController | null = null;
  private cardCountAtTriggerTime = 0;
  private boundOnDrag: (event: PointerEvent) => void;
  private boundEndDrag: (event: PointerEvent) => void;
  private boundHandleKeydown: (event: KeyboardEvent) => void;
  private boundOnFrameRender: (event: Event) => void;
  private boundOnFrameLoad: (event: Event) => void;
  private boundOnFetchError: (event: Event) => void;
  private boundOnBeforeCache: () => void;
  private boundOnBeforeFetch: (event: Event) => void;
  private boundOnPopstate: () => void;

  connect(): void {
    // Bind methods for event listeners
    this.boundOnDrag = this.onDrag.bind(this);
    this.boundEndDrag = this.endDrag.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundOnFrameRender = this.handleFrameRender.bind(this);
    this.boundOnFrameLoad = this.handleFrameLoad.bind(this);
    this.boundOnFetchError = this.handleFetchError.bind(this);
    this.boundOnBeforeCache = this.handleBeforeCache.bind(this);
    this.boundOnBeforeFetch = this.handleBeforeFetch.bind(this);
    this.boundOnPopstate = this.handlePopstate.bind(this);

    // Listen for ESC key to close bottom sheet
    document.addEventListener("keydown", this.boundHandleKeydown);

    // Path A: Turbo frame request lifecycle for #catalog-results.
    // before-fetch-request fires before the network call so the loading state
    // shows immediately on pagination clicks (which don't go through submit()).
    // before-frame-render + frame-load bracket the DOM swap as a safety net.
    document.addEventListener("turbo:before-fetch-request", this.boundOnBeforeFetch);
    document.addEventListener("turbo:before-frame-render", this.boundOnFrameRender);
    document.addEventListener("turbo:frame-load", this.boundOnFrameLoad);
    document.addEventListener("turbo:fetch-request-error", this.boundOnFetchError);

    // Close bottom sheet on Turbo cache (prevents stale state)
    document.addEventListener("turbo:before-cache", this.boundOnBeforeCache);

    // Back/forward nav: Turbo only advances history, it doesn't re-render our
    // stream-based updates. Re-fetch on popstate so content matches the URL.
    window.addEventListener("popstate", this.boundOnPopstate);

    // Restore focus if we have a pending restore (after Turbo frame update)
    if (pendingFocusRestore) {
      requestAnimationFrame(() => {
        if (pendingFocusRestore) {
          const targetInput = this.getVisibleSearchInput();
          if (targetInput) {
            targetInput.focus();
            const pos = Math.min(pendingFocusRestore.cursorPos, targetInput.value.length);
            targetInput.setSelectionRange(pos, pos);
          }
          pendingFocusRestore = null;
        }
      });
    }

    // Ensure loading state is cleared on connect (fresh page load)
    this.hideLoading();

    if (this.hasModeBarTarget) {
      const willBeVisible = this.modeBarTarget.classList.contains("is-visible-initial");
      if (willBeVisible) {
        this.modeBarTarget.classList.remove("is-visible-initial");
        if (modeBarWasVisible) {
          // Bar was already visible before this reconnect (e.g. user switched
          // modes within the same modality). Apply is-visible synchronously
          // without the entrance transition so tabs don't re-fade-in.
          this.showModeBarWithoutAnimation();
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.modeBarTarget.classList.add("is-visible");
            });
          });
        }
      }
      modeBarWasVisible = willBeVisible;
    } else {
      modeBarWasVisible = false;
    }
  }

  private showModeBarWithoutAnimation(): void {
    const bar = this.modeBarTarget;
    const tabs = Array.from(bar.querySelectorAll<HTMLElement>(".catalog-mode-tab"));
    bar.style.transition = "none";
    tabs.forEach((t) => (t.style.transition = "none"));
    bar.classList.add("is-visible");
    // Force reflow so the transition: none takes effect before we restore it.
    void bar.offsetHeight;
    requestAnimationFrame(() => {
      bar.style.transition = "";
      tabs.forEach((t) => (t.style.transition = ""));
    });
  }

  disconnect(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.clearTransitionTimers();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    document.removeEventListener("keydown", this.boundHandleKeydown);
    document.removeEventListener("turbo:before-fetch-request", this.boundOnBeforeFetch);
    document.removeEventListener("turbo:before-frame-render", this.boundOnFrameRender);
    document.removeEventListener("turbo:frame-load", this.boundOnFrameLoad);
    document.removeEventListener("turbo:fetch-request-error", this.boundOnFetchError);
    document.removeEventListener("turbo:before-cache", this.boundOnBeforeCache);
    window.removeEventListener("popstate", this.boundOnPopstate);
  }

  // === SEARCH DEBOUNCE ===
  debounceSearch(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      this.submit();
    }, this.DEBOUNCE_DELAY);
  }

  // === FORM SUBMISSION ===
  submit(): void {
    const form = this.getVisibleSearchForm();
    if (!form) return;

    // Save focus state if a search input is focused
    for (const input of this.searchInputTargets) {
      if (document.activeElement === input) {
        pendingFocusRestore = {
          cursorPos: input.selectionStart || input.value.length,
        };
        break;
      }
    }

    // Sync search values across all search inputs before submitting
    const searchValue = this.getVisibleSearchInput()?.value || "";
    this.searchInputTargets.forEach((input) => (input.value = searchValue));

    // Update hidden fields in mobile search form to match current URL params
    // This ensures filters aren't lost when searching on mobile
    this.syncHiddenFieldsWithUrl(form);

    this.showLoading();
    form.requestSubmit();
  }

  /** Sync hidden fields in a form with current URL params to preserve filter state.
   *  Only affects forms that use hidden fields (mobile search form), not forms
   *  with actual select elements (desktop form). */
  private syncHiddenFieldsWithUrl(form: HTMLFormElement): void {
    const currentParams = new URLSearchParams(window.location.search);
    const filterKeys = ["creator", "status", "pricing", "mode", "sort", "modality"];

    for (const key of filterKeys) {
      // Skip if form has a select for this key (desktop form) - no sync needed
      if (form.querySelector(`select[name="${key}"]`)) continue;

      const urlValue = currentParams.get(key);
      let hiddenField = form.querySelector<HTMLInputElement>(`input[type="hidden"][name="${key}"]`);

      if (urlValue) {
        // URL has this param - ensure hidden field exists with correct value
        if (hiddenField) {
          hiddenField.value = urlValue;
        } else {
          hiddenField = document.createElement("input");
          hiddenField.type = "hidden";
          hiddenField.name = key;
          hiddenField.value = urlValue;
          form.appendChild(hiddenField);
        }
      } else if (hiddenField) {
        // URL doesn't have this param - remove stale hidden field
        hiddenField.remove();
      }
    }
  }

  /** Returns the first visible search form */
  private getVisibleSearchForm(): HTMLFormElement | null {
    for (const form of this.searchFormTargets) {
      if (this.isElementVisible(form)) return form;
    }
    return null;
  }

  /** Returns the first visible search input */
  private getVisibleSearchInput(): HTMLInputElement | null {
    for (const input of this.searchInputTargets) {
      if (this.isElementVisible(input)) return input;
    }
    return null;
  }

  /** Check if element is visible (not display:none) */
  private isElementVisible(element: HTMLElement): boolean {
    return element.offsetParent !== null;
  }

  // === LOADING STATE ===
  //
  // The results wrapper lives inside the `#catalog-results` Turbo frame, which
  // is a sibling of the `#catalog-filters` frame that owns this controller —
  // so Stimulus targets can't reach it. We query the DOM directly each call
  // (the wrapper element is replaced on every frame render, so caching is
  // unsafe anyway).

  private getResultsWrapper(): HTMLElement | null {
    return document.querySelector<HTMLElement>("#catalog-results .catalog-results");
  }

  private getSkeletonTemplate(): HTMLTemplateElement | null {
    return document.querySelector<HTMLTemplateElement>(
      "#catalog-results .catalog-results > template",
    );
  }

  /** Enter the dimmed state and arm the skeleton timer. */
  private showLoading(): void {
    const wrapper = this.getResultsWrapper();
    if (!wrapper) return;

    // Cancel any in-progress transition (rapid re-trigger).
    this.clearTransitionTimers();

    // Capture current card count for skeleton rendering if we escalate.
    const grid = wrapper.querySelector<HTMLElement>(".grid");
    this.cardCountAtTriggerTime = grid ? grid.children.length : 0;

    wrapper.classList.remove("is-skeleton", "is-revealing");
    wrapper.classList.add("is-dimmed");
    wrapper.setAttribute("aria-busy", "true");

    // Arm the skeleton escalation.
    this.skeletonTimer = window.setTimeout(() => {
      this.enterSkeletonState();
    }, this.SKELETON_DELAY_MS);
  }

  /** Clear loading state and run the reveal animation. */
  private hideLoading(): void {
    const wrapper = this.getResultsWrapper();
    if (!wrapper) return;

    this.clearTransitionTimers();

    wrapper.classList.remove("is-dimmed", "is-skeleton");
    wrapper.classList.add("is-revealing");
    wrapper.setAttribute("aria-busy", "false");

    this.revealTimer = window.setTimeout(() => {
      wrapper.classList.remove("is-revealing");
      this.revealTimer = null;
    }, this.REVEAL_DURATION_MS);
  }

  /** Swap the grid children for N skeleton clones. Called at T+SKELETON_DELAY_MS
   *  if the request hasn't resolved yet. */
  private enterSkeletonState(): void {
    this.skeletonTimer = null;
    const wrapper = this.getResultsWrapper();
    const template = this.getSkeletonTemplate();
    if (!wrapper || !template) return;

    const grid = wrapper.querySelector<HTMLElement>(".grid");
    if (!grid) return;

    const skeletonNode = template.content.firstElementChild;
    if (!skeletonNode) return;

    const count = Math.max(this.cardCountAtTriggerTime, 1);
    grid.replaceChildren();
    for (let i = 0; i < count; i += 1) {
      grid.appendChild(skeletonNode.cloneNode(true));
    }

    wrapper.classList.remove("is-dimmed");
    wrapper.classList.add("is-skeleton");
  }

  private clearTransitionTimers(): void {
    if (this.skeletonTimer !== null) {
      window.clearTimeout(this.skeletonTimer);
      this.skeletonTimer = null;
    }
    if (this.revealTimer !== null) {
      window.clearTimeout(this.revealTimer);
      this.revealTimer = null;
    }
  }

  // === TURBO EVENT HANDLERS (Path A) ===

  private handleFrameRender(event: Event): void {
    const frame = event.target as HTMLElement | null;
    if (!frame || frame.id !== "catalog-results") return;
    // Fires just before the old frame body is replaced. We may already be
    // in is-dimmed from the manual showLoading() in submit(); this is the
    // safety net for pagination clicks (which don't go through submit()).
    this.showLoading();
  }

  private handleFrameLoad(event: Event): void {
    const frame = event.target as HTMLElement | null;
    if (!frame || frame.id !== "catalog-results") return;
    this.hideLoading();
  }

  private handleFetchError(event: Event): void {
    // Scope by dispatch target: Turbo fires this on the frame for frame-scoped
    // fetches. If the target isn't our frame, it isn't our concern.
    const frame = event.target as HTMLElement | null;
    if (!frame || frame.id !== "catalog-results") return;
    this.hideLoading();
  }

  private handleBeforeCache(): void {
    if (this.isBottomSheetOpen()) {
      this.closeBottomSheet();
    }
  }

  private handleBeforeFetch(event: Event): void {
    // Fires before any Turbo fetch. For frame-scoped fetches (including
    // pagination links), event.target is the frame. For full-page visits it's
    // the document — ignore those.
    const target = event.target as HTMLElement | null;
    if (!target || target.id !== "catalog-results") return;
    this.showLoading();
  }

  private handlePopstate(): void {
    // Back/forward navigation changed the URL without re-rendering; sync the
    // content. Pass skipAdvance so we don't push a duplicate history entry.
    if (!window.location.pathname.startsWith("/models")) return;
    const url = new URL(window.location.href);
    this.loadFrames(url, { skipAdvance: true });
  }

  // === CLEAR SEARCH ===
  clearSearch(): void {
    this.searchInputTargets.forEach((input) => (input.value = ""));
    this.submit();
  }

  // === BOTTOM SHEET CONTROLS ===
  openBottomSheet(): void {
    if (!this.hasBackdropTarget || !this.hasBottomSheetTarget) return;

    // Show backdrop
    this.backdropTarget.classList.remove("hidden");
    requestAnimationFrame(() => {
      this.backdropTarget.classList.remove("opacity-0");
      this.backdropTarget.classList.add("opacity-100");
    });

    // Slide up sheet
    requestAnimationFrame(() => {
      this.bottomSheetTarget.classList.remove("translate-y-full");
      this.bottomSheetTarget.classList.add("translate-y-0");
    });

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  closeBottomSheet(): void {
    if (!this.hasBackdropTarget || !this.hasBottomSheetTarget) return;

    // Hide backdrop
    this.backdropTarget.classList.remove("opacity-100");
    this.backdropTarget.classList.add("opacity-0");

    // Slide down sheet
    this.bottomSheetTarget.classList.remove("translate-y-0");
    this.bottomSheetTarget.classList.add("translate-y-full");

    // Re-enable body scroll
    document.body.style.overflow = "";

    // Hide backdrop after animation completes
    setTimeout(() => {
      this.backdropTarget.classList.add("hidden");
    }, 300);
  }

  // === DRAG TO DISMISS ===
  startDrag(event: PointerEvent): void {
    this.isDragging = true;
    this.dragStartY = event.clientY;

    // Disable transition during drag for immediate feedback
    this.bottomSheetTarget.classList.add("dragging");

    document.addEventListener("pointermove", this.boundOnDrag);
    document.addEventListener("pointerup", this.boundEndDrag);
  }

  private onDrag(event: PointerEvent): void {
    if (!this.isDragging) return;

    const deltaY = event.clientY - this.dragStartY;
    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      this.bottomSheetTarget.style.transform = `translateY(${deltaY}px)`;
    }
  }

  private endDrag(event: PointerEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;

    // Re-enable transition
    this.bottomSheetTarget.classList.remove("dragging");
    this.bottomSheetTarget.style.transform = "";

    const deltaY = event.clientY - this.dragStartY;
    const threshold = 100; // pixels to trigger close

    if (deltaY > threshold) {
      this.closeBottomSheet();
    } else {
      // Snap back to open position
      this.bottomSheetTarget.classList.add("translate-y-0");
    }

    document.removeEventListener("pointermove", this.boundOnDrag);
    document.removeEventListener("pointerup", this.boundEndDrag);
  }

  // === MOBILE APPLY ===
  applyMobileFilters(event: Event): void {
    event.preventDefault();

    if (this.hasMobileFormTarget) {
      // Sync search value from URL to the bottom sheet form's hidden field
      this.syncSearchValueWithUrl(this.mobileFormTarget);
      // Mirror desktop submit(): trigger loading state explicitly. The
      // turbo:before-fetch-request listener targets the #catalog-results
      // frame, but for cross-frame form submissions the event fires on the
      // form, not the frame — so the listener doesn't catch it.
      this.showLoading();
      this.mobileFormTarget.requestSubmit();
    }

    this.closeBottomSheet();
  }

  /** Fetch a URL expecting a turbo-stream response and render it. URL advance
   *  happens via the <turbo-stream action="advance"> element in the response.
   *  Path B: turbo:frame-load does NOT fire for renderStreamMessage, so we
   *  explicitly manage showLoading()/hideLoading() here.
   *
   *  Pass `skipAdvance: true` on popstate-triggered loads so we don't push a
   *  duplicate history entry over the one the browser just restored. */
  private async loadFrames(url: URL, options: { skipAdvance?: boolean } = {}): Promise<void> {
    // Cancel any prior in-flight stream request (rapid modality/mode clicks).
    if (this.abortController) {
      this.abortController.abort();
    }
    const controller = new AbortController();
    this.abortController = controller;

    // Fetch from the `.turbo_stream` format extension path so Cloudflare keys
    // this response separately from the bare HTML URL. Both variants stay
    // CDN-cacheable that way; sharing a URL would let the cached stream body
    // poison plain-HTML refreshes (see PublicCaching safety net).
    const streamUrl = new URL(url.toString());
    if (!streamUrl.pathname.endsWith(".turbo_stream")) {
      streamUrl.pathname = `${streamUrl.pathname}.turbo_stream`;
    }

    this.showLoading();
    try {
      const response = await fetch(streamUrl.toString(), {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        credentials: "same-origin",
        signal: controller.signal,
      });
      if (!response.ok) {
        // Fall back to full visit if the server didn't return a stream.
        Turbo.visit(url.toString());
        return;
      }
      let text = await response.text();
      if (options.skipAdvance) {
        text = text.replace(
          /<turbo-stream[^>]*action="advance"[^>]*>\s*<\/turbo-stream>/g,
          "",
        );
      }
      Turbo.renderStreamMessage(text);
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") {
        // Superseded by a newer request; newer call will manage state.
        return;
      }
      throw err;
    } finally {
      if (this.abortController === controller) {
        this.abortController = null;
        this.hideLoading();
      }
    }
  }

  /** Sync the search (q) hidden field with current URL param */
  private syncSearchValueWithUrl(form: HTMLFormElement): void {
    const currentParams = new URLSearchParams(window.location.search);
    const searchValue = currentParams.get("q");
    let hiddenField = form.querySelector<HTMLInputElement>('input[type="hidden"][name="q"]');

    if (searchValue) {
      if (hiddenField) {
        hiddenField.value = searchValue;
      } else {
        hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = "q";
        hiddenField.value = searchValue;
        form.appendChild(hiddenField);
      }
    } else if (hiddenField) {
      hiddenField.remove();
    }
  }

  // === MODALITY SWITCHER ===
  async selectModality(event: Event): Promise<void> {
    const button = event.currentTarget as HTMLButtonElement;
    const newModality = button.dataset.modality || "";
    const currentParams = new URLSearchParams(window.location.search);
    const currentModality = currentParams.get("modality") || "";

    const url = new URL(window.location.href);
    if (newModality) {
      url.searchParams.set("modality", newModality);
    } else {
      url.searchParams.delete("modality");
    }
    url.searchParams.delete("mode");
    url.searchParams.delete("page");

    const wasVisible = currentModality === "image" || currentModality === "video";
    const willBeVisible = newModality === "image" || newModality === "video";
    const shouldAnimateExit =
      wasVisible && !willBeVisible && this.hasModeBarTarget;

    if (shouldAnimateExit) {
      this.modeBarTarget.classList.remove("is-visible");
      await new Promise((resolve) => window.setTimeout(resolve, 260));
    }

    await this.loadFrames(url);
  }

  // === MODE TAB ===
  async selectMode(event: Event): Promise<void> {
    const button = event.currentTarget as HTMLButtonElement;
    const mode = button.dataset.mode || "";

    const url = new URL(window.location.href);
    if (mode) {
      url.searchParams.set("mode", mode);
    } else {
      url.searchParams.delete("mode");
    }
    url.searchParams.delete("page");

    await this.loadFrames(url);
  }

  // === PILL REMOVAL ===
  removePill(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement;
    const filterKey = button.dataset.filterKey;

    if (!filterKey) return;

    // Animate pill out
    const pill = button.closest(".filter-pill") as HTMLElement;
    if (pill) {
      pill.classList.add("removing");
      pill.addEventListener(
        "animationend",
        () => {
          // Build URL without this filter
          const url = new URL(window.location.href);
          url.searchParams.delete(filterKey);

          // Full page navigation so pills update
          Turbo.visit(url.toString(), { action: "replace" });
        },
        { once: true }
      );
    }
  }

  // === KEYBOARD HANDLING ===
  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.isBottomSheetOpen()) {
      this.closeBottomSheet();
    }
  }

  private isBottomSheetOpen(): boolean {
    return (
      this.hasBottomSheetTarget &&
      this.bottomSheetTarget.classList.contains("translate-y-0")
    );
  }
}
